import type { Placement, ResponsiveValue } from '@chakra-ui/react'
import {
  Tooltip as ChakraTooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'

/**
 * Tooltip component that adapts based on device type and interactivity requirements.
 * On mobile devices or when isInteractive is true, uses Popover for click/tap interaction.
 * On desktop with non-interactive tooltips, uses standard Chakra Tooltip for hover.
 *
 * IMPORTANT: To ensure proper ARIA attributes and accessibility, children should be
 * interactive elements like Button or IconButton. Avoid wrapping children in layout
 * components (Box, Flex, VStack, etc.) as this can cause ARIA validation errors.
 */
export interface TooltipProps {
  /** The element to attach the tooltip to. Should be an interactive element (Button, IconButton) for proper accessibility. */
  children: React.ReactNode
  /** The content to display in the tooltip */
  label: string | React.ReactNode
  /** Placement of the tooltip relative to the trigger element */
  placement?: Placement
  /** Whether to show an arrow pointing to the trigger element */
  hasArrow?: boolean
  /** Background color in light mode */
  bgLight?: string
  /** Text color in light mode */
  colorLight?: string
  /** Background color in dark mode */
  bgDark?: string
  /** Text color in dark mode */
  colorDark?: string
  /** Font size of the tooltip content */
  size?: ResponsiveValue<string>
  /** Whether the tooltip content is interactive (contains links or buttons). Forces Popover mode. */
  isInteractive?: boolean
  /** Delay in milliseconds before closing the tooltip */
  closeDelay?: number
  /** Maximum width of the tooltip */
  maxW?: string
  /** Minimum width of the tooltip */
  minW?: string
}

export const Tooltip = ({
  children,
  label,
  placement = 'bottom',
  size,
  hasArrow = true,
  bgLight = 'bg.band',
  maxW = '400px',
  minW = 'auto',
  colorLight = 'text.body',
  bgDark = 'bg.band',
  colorDark = 'text.body',
  isInteractive = false,
  closeDelay = 500,
}: TooltipProps) => {
  const tooltipBg = useColorModeValue(bgLight, bgDark)
  const tooltipColor = useColorModeValue(colorLight, colorDark)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isMobile, setIsMobile] = useState(false)

  // Detect if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0)
    }
    checkMobile()
    // Re-check on window resize in case device orientation changes
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Use Popover for interactive tooltips or mobile devices
  // On mobile, we need click/tap interaction since hover doesn't exist
  if (isInteractive || isMobile) {
    return (
      <Popover
        trigger={isMobile ? 'click' : 'hover'}
        placement={placement}
        closeDelay={closeDelay}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      >
        <PopoverTrigger>{children}</PopoverTrigger>
        <PopoverContent
          bg={tooltipBg}
          color={tooltipColor}
          borderColor="line.dark"
          maxW={maxW}
          fontSize={size}
        >
          {hasArrow && <PopoverArrow bg={tooltipBg} boxShadow="none" />}
          <PopoverBody whiteSpace="pre-wrap" textAlign="left" px={3} py={2}>
            {label}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    )
  }

  // Use standard Tooltip for non-interactive tooltips on desktop
  return (
    <ChakraTooltip
      as="div"
      size={size}
      label={label}
      maxW={maxW}
      minW={minW}
      whiteSpace="pre-wrap"
      placement={placement}
      hasArrow={hasArrow}
      bg={tooltipBg}
      color={tooltipColor}
    >
      {children}
    </ChakraTooltip>
  )
}
